import { useState, useEffect, useCallback } from 'react';

const TIMESLOTS_API =
  'https://readdy.ai/api/public/calendar/timeslots/c0d89ed2-f31f-4e5e-9090-962c69481199.7d3a8d3e8ed777f6d0796c175efae344934ad41f02ace5089b706af69e4cf7c7';
const APPOINTMENTS_API =
  'https://readdy.ai/api/public/calendar/appointments/c0d89ed2-f31f-4e5e-9090-962c69481199.7d3a8d3e8ed777f6d0796c175efae344934ad41f02ace5089b706af69e4cf7c7';

interface Timeslot {
  start_time: string;
  end_time: string;
}

interface FormState {
  customer_name: string;
  customer_phone: string;
  notes: string;
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

function extractDate(dt: string): string {
  return dt.split(' ')[0];
}

function extractTime(dt: string): string {
  const parts = dt.split(' ');
  if (parts.length < 2) return '';
  return parts[1].slice(0, 5);
}

function todayString(): string {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function getClosestAvailableDate(dates: string[]): string | null {
  const today = todayString();
  const future = dates.filter((d) => d >= today).sort();
  if (future.length > 0) return future[0];
  const past = [...dates].sort();
  return past.length > 0 ? past[past.length - 1] : null;
}

function buildCalendarGrid(year: number, month: number): (number | null)[] {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const grid: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++) grid.push(d);
  while (grid.length % 7 !== 0) grid.push(null);
  return grid;
}

function padTwo(n: number): string {
  return String(n).padStart(2, '0');
}

function dateFromYMD(year: number, month: number, day: number): string {
  return `${year}-${padTwo(month + 1)}-${padTwo(day)}`;
}

export default function BookingCalendar() {
  const [step, setStep] = useState<0 | 1 | 2>(0);
  const [timeslots, setTimeslots] = useState<Timeslot[]>([]);
  const [loading, setLoading] = useState(true);
  const [calYear, setCalYear] = useState<number>(new Date().getFullYear());
  const [calMonth, setCalMonth] = useState<number>(new Date().getMonth());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<Timeslot | null>(null);
  const [form, setForm] = useState<FormState>({ customer_name: '', customer_phone: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const fetchTimeslots = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(TIMESLOTS_API);
      const data: Timeslot[] = await res.json();
      setTimeslots(Array.isArray(data) ? data : []);
    } catch {
      setTimeslots([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTimeslots();
  }, [fetchTimeslots]);

  // Auto-navigate calendar to closest available date on load
  useEffect(() => {
    if (timeslots.length === 0) return;
    const dates = [...new Set(timeslots.map((s) => extractDate(s.start_time)))];
    const closest = getClosestAvailableDate(dates);
    if (closest) {
      const [y, m] = closest.split('-').map(Number);
      setCalYear(y);
      setCalMonth(m - 1);
      setSelectedDate(closest);
    }
  }, [timeslots]);

  const availableDates = new Set(timeslots.map((s) => extractDate(s.start_time)));

  const slotsForDate = timeslots.filter(
    (s) => extractDate(s.start_time) === selectedDate
  );

  const grid = buildCalendarGrid(calYear, calMonth);

  const prevMonth = () => {
    if (calMonth === 0) { setCalYear((y) => y - 1); setCalMonth(11); }
    else setCalMonth((m) => m - 1);
  };

  const nextMonth = () => {
    if (calMonth === 11) { setCalYear((y) => y + 1); setCalMonth(0); }
    else setCalMonth((m) => m + 1);
  };

  const handleDateSelect = (day: number) => {
    const dateStr = dateFromYMD(calYear, calMonth, day);
    if (!availableDates.has(dateStr)) return;
    setSelectedDate(dateStr);
    setSelectedSlot(null);
    setStep(1);
  };

  const handleSlotSelect = (slot: Timeslot) => {
    setSelectedSlot(slot);
    setStep(2);
  };

  const handleModifyTime = () => {
    setStep(0);
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedSlot) return;
    setSubmitting(true);
    setSubmitStatus('idle');
    setErrorMsg('');

    const payload: Record<string, string> = {
      customer_name: form.customer_name,
      customer_phone: form.customer_phone,
      start_time: selectedSlot.start_time,
      end_time: selectedSlot.end_time,
      notes: form.notes,
    };

    try {
      const res = await fetch(APPOINTMENTS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Booking failed');
      setSubmitStatus('success');
      await fetchTimeslots();
      setSelectedDate(null);
      setSelectedSlot(null);
      setForm({ customer_name: '', customer_phone: '', notes: '' });
      setStep(0);
    } catch (err: unknown) {
      setSubmitStatus('error');
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-0 py-3 text-stone-900 placeholder-stone-400 border-0 border-b border-stone-300 focus:border-stone-700 focus:outline-none bg-transparent font-light text-sm';

  return (
    <div className="w-full">
      {/* Step indicator */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {['Select Date', 'Select Time', 'Confirm & Book'].map((label, i) => (
          <div key={i} className="flex items-center gap-2">
            <div
              className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium transition-colors ${
                step === i
                  ? 'bg-stone-800 text-white'
                  : step > i
                  ? 'bg-stone-400 text-white'
                  : 'bg-stone-200 text-stone-500'
              }`}
            >
              {step > i ? <i className="ri-check-line text-xs"></i> : i + 1}
            </div>
            <span
              className={`text-xs font-light hidden sm:inline ${
                step === i ? 'text-stone-800' : 'text-stone-400'
              }`}
            >
              {label}
            </span>
            {i < 2 && <div className="w-8 h-px bg-stone-300 mx-1" />}
          </div>
        ))}
      </div>

      {/* STEP 0 — Calendar */}
      {step === 0 && (
        <div className="w-full">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-stone-400 font-light text-sm">
              <i className="ri-loader-4-line animate-spin mr-2"></i> Loading availability…
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
              {/* Month nav */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-stone-100">
                <button
                  onClick={prevMonth}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer text-stone-600"
                >
                  <i className="ri-arrow-left-s-line"></i>
                </button>
                <span className="text-sm font-medium text-stone-800">
                  {MONTHS[calMonth]} {calYear}
                </span>
                <button
                  onClick={nextMonth}
                  className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer text-stone-600"
                >
                  <i className="ri-arrow-right-s-line"></i>
                </button>
              </div>

              {/* Day headers */}
              <div className="grid grid-cols-7 border-b border-stone-100">
                {DAYS.map((d) => (
                  <div key={d} className="py-2 text-center text-xs font-medium text-stone-400 uppercase tracking-wide">
                    {d}
                  </div>
                ))}
              </div>

              {/* Grid */}
              <div className="grid grid-cols-7">
                {grid.map((day, idx) => {
                  if (day === null) {
                    return <div key={`empty-${idx}`} className="h-12" />;
                  }
                  const dateStr = dateFromYMD(calYear, calMonth, day);
                  const isAvailable = availableDates.has(dateStr);
                  const isSelected = selectedDate === dateStr;
                  const isToday = dateStr === todayString();

                  return (
                    <button
                      key={dateStr}
                      onClick={() => handleDateSelect(day)}
                      disabled={!isAvailable}
                      className={`h-12 flex flex-col items-center justify-center text-xs transition-all cursor-pointer relative
                        ${isSelected ? 'bg-stone-800 text-white rounded-lg' : ''}
                        ${isAvailable && !isSelected ? 'hover:bg-stone-100 text-stone-800 font-medium' : ''}
                        ${!isAvailable ? 'text-stone-300 cursor-not-allowed' : ''}
                      `}
                    >
                      {isToday && !isSelected && (
                        <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-stone-400" />
                      )}
                      <span>{day}</span>
                      {isAvailable && !isSelected && (
                        <span className="w-1 h-1 rounded-full bg-stone-500 mt-0.5" />
                      )}
                    </button>
                  );
                })}
              </div>

              <div className="px-6 py-3 border-t border-stone-100 flex items-center gap-4 text-xs text-stone-400 font-light">
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-stone-500 inline-block" /> Available
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-stone-800 inline-block" /> Selected
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* STEP 1 — Time slots */}
      {step === 1 && (
        <div className="w-full">
          <div className="flex items-center gap-3 mb-5">
            <button
              onClick={() => setStep(0)}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer text-stone-600"
            >
              <i className="ri-arrow-left-line text-sm"></i>
            </button>
            <div>
              <p className="text-xs text-stone-400 font-light">Selected date</p>
              <p className="text-sm font-medium text-stone-800">{selectedDate}</p>
            </div>
          </div>

          {slotsForDate.length === 0 ? (
            <p className="text-stone-400 font-light text-sm text-center py-8">
              No available time slots for this date.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {slotsForDate.map((slot, i) => (
                <button
                  key={i}
                  onClick={() => handleSlotSelect(slot)}
                  className="bg-white border border-stone-200 rounded-lg px-4 py-3 text-center hover:border-stone-700 hover:bg-stone-50 transition-all cursor-pointer group"
                >
                  <p className="text-sm font-medium text-stone-800 group-hover:text-stone-900">
                    {extractTime(slot.start_time)}
                  </p>
                  <p className="text-xs text-stone-400 font-light mt-0.5">
                    – {extractTime(slot.end_time)}
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* STEP 2 — Review + Form */}
      {step === 2 && selectedSlot && (
        <div className="w-full">
          {/* Review block */}
          <div className="bg-stone-50 border border-stone-200 rounded-xl p-5 mb-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs text-stone-400 font-light uppercase tracking-wide mb-2">Your Appointment</p>
                <div className="flex items-center gap-2 text-stone-800">
                  <i className="ri-calendar-line text-stone-500 text-sm"></i>
                  <span className="text-sm font-medium">{selectedDate}</span>
                </div>
                <div className="flex items-center gap-2 text-stone-800 mt-1.5">
                  <i className="ri-time-line text-stone-500 text-sm"></i>
                  <span className="text-sm font-medium">
                    {extractTime(selectedSlot.start_time)} – {extractTime(selectedSlot.end_time)}
                  </span>
                </div>
              </div>
              <button
                onClick={handleModifyTime}
                className="text-xs text-stone-500 hover:text-stone-800 underline underline-offset-2 cursor-pointer font-light transition-colors whitespace-nowrap"
              >
                Modify time
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <input
                type="text"
                name="customer_name"
                placeholder="Full Name *"
                value={form.customer_name}
                onChange={handleFormChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <input
                type="tel"
                name="customer_phone"
                placeholder="Phone Number *"
                value={form.customer_phone}
                onChange={handleFormChange}
                required
                className={inputClass}
              />
            </div>
            <div>
              <textarea
                name="notes"
                placeholder="Briefly describe your legal matter (optional)"
                value={form.notes}
                onChange={handleFormChange}
                rows={3}
                maxLength={500}
                className={`${inputClass} resize-none`}
              />
              <div className="text-right text-xs text-stone-400 mt-1">{form.notes.length}/500</div>
            </div>

            <div className="pt-2">
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-stone-800 text-stone-50 px-8 py-4 text-sm font-medium hover:bg-stone-900 transition-colors whitespace-nowrap cursor-pointer rounded-full disabled:opacity-50"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i> Booking…
                  </span>
                ) : (
                  'Confirm Booking'
                )}
              </button>
            </div>

            {submitStatus === 'success' && (
              <div className="flex items-center gap-2 text-green-600 text-sm font-light bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                <i className="ri-checkbox-circle-line"></i>
                Booking confirmed! We'll be in touch shortly.
              </div>
            )}
            {submitStatus === 'error' && (
              <div className="flex items-center gap-2 text-red-600 text-sm font-light bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                <i className="ri-error-warning-line"></i>
                {errorMsg || 'Something went wrong. Please try again.'}
              </div>
            )}
          </form>
        </div>
      )}
    </div>
  );
}
